import { z } from 'zod';
/**
 * Chat für die eigene Webseite (Einrichtung → APIs und Webhooks → Chat (Webseite)). Der Mandant legt dort selbst Schlüssel für seine Webseite an (Bereich `webchat:visitor`, sie stehen im Seitenquelltext und
 * dürfen deshalb sonst nichts), trägt ein, auf welchen Webseiten sie gelten, und bekommt den Code zum Einbinden und das Plugin zum Herunterladen (WordPress und für alle anderen Webseiten).
 */
export const CHAT_EMBED_MAX_KEYS = 5;
export const CHAT_EMBED_MAX_ORIGINS = 10;
/** Version des Chat-Plugins und der Dateien in den Downloads. Eine Änderung am Baustein (`wirkia-chat.js`) oder am Plugin zählt sie hoch. */
export const CHAT_PLUGIN_VERSION = '1.3.0';
export const CHAT_PLUGIN_FILES = {
    wordpress: 'wirkia-chat-wordpress.zip',
    universal: 'wirkia-chat-webseite.zip',
};
/** Eine Herkunft ist Protokoll, Rechnername und optional Port, ohne Pfad: https://www.beispiel.de. Eingaben wie „www.beispiel.de/kontakt“ bringt Wirkia in diese Form. */
export function normalizeOrigin(input) {
    let text = input.trim().toLowerCase();
    if (!text)
        return null;
    if (!/^[a-z]+:\/\//.test(text))
        text = `https://${text}`;
    const m = /^(https?):\/\/([a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*)(:\d{1,5})?(?:[/?#].*)?$/.exec(text);
    if (!m)
        return null;
    const host = m[2];
    // Ein Rechnername ohne Punkt ist nur auf dem eigenen Rechner sinnvoll (localhost, Entwicklung)
    if (!host.includes('.') && host !== 'localhost')
        return null;
    return `${m[1]}://${host}${m[6] ?? ''}`;
}
const origin = z
    .string()
    .trim()
    .max(200)
    .transform((v, ctx) => {
    const n = normalizeOrigin(v);
    if (!n)
        ctx.addIssue({ code: 'custom', message: `„${v}“ ist keine Webadresse. Bitte geben Sie sie so an: https://www.beispiel.de` });
    return n ?? '';
});
const origins = z
    .array(origin)
    .max(CHAT_EMBED_MAX_ORIGINS, `Höchstens ${CHAT_EMBED_MAX_ORIGINS} Webseiten je Schlüssel`)
    .transform((list) => [...new Set(list)]);
export const createChatKeyInput = z
    .object({
    name: z.string().trim().min(2, 'Bitte geben Sie dem Schlüssel einen Namen').max(80),
    /** Webseiten, auf denen der Schlüssel gilt. Leer: auf jeder. */
    allowedOrigins: origins.default([]),
})
    .strict();
export const updateChatKeyInput = z
    .object({
    name: z.string().trim().min(2, 'Bitte geben Sie dem Schlüssel einen Namen').max(80).optional(),
    allowedOrigins: origins.optional(),
})
    .strict()
    .refine((v) => v.name !== undefined || v.allowedOrigins !== undefined, 'Keine Änderung angegeben');
const attr = (name, value) => (value ? ` data-${name}="${value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')}"` : '');
/** Der Code, den man in die Webseite einfügt: eine Zeile, vor dem Ende des body */
export function chatSnippet(info, o = {}) {
    return `<script src="${info.scriptUrl}" data-api="${info.apiUrl}" data-key="${info.key}"${attr('title', o.title)}${attr('greeting', o.greeting)}${attr('color', o.color)}${attr('privacy-url', o.privacyUrl)}${attr('support-email', o.supportEmail)}${attr('hours', o.hours)}${attr('days', o.days)} defer></script>`;
}
// ---------------------------------------------------------------------------
// Einstellungen des Chat-Fensters je Mandant (Einrichtung → APIs und Webhooks → Chat (Webseite))
// ---------------------------------------------------------------------------
/** Was das Chat-Fenster zeigt, wenn der Mandant nichts einstellt */
export const CHAT_WIDGET_DEFAULTS = {
    title: 'Chat',
    greeting: 'Wie können wir helfen? Schreiben Sie uns, wir antworten in der Regel innerhalb weniger Minuten.',
    color: '#0f766e',
    timezone: 'Europe/Berlin',
};
/**
 * Text, den der Besucher als Nachricht im Chat bekommt, wenn ein anderer Mitarbeiter die Unterhaltung übernimmt. Die Angaben in geschweiften Klammern ersetzt Wirkia:
 * {mitarbeiter} und {vorheriger} durch den Vornamen, {mitarbeiter_name} und {vorheriger_name} durch den vollen Namen des neuen und des bisherigen Ansprechpartners.
 */
export const CHAT_TRANSFER_DEFAULT = 'Sie wurden an {mitarbeiter} weitergeleitet. Ihre bisherigen Nachrichten stehen {mitarbeiter} weiterhin zur Verfügung, Sie müssen Ihr Anliegen nicht noch einmal erklären. Bitte geben Sie {mitarbeiter} einen Moment Zeit, sich in Ihre Angelegenheit einzulesen, damit Ihnen bestmöglich geholfen werden kann.';
export const CHAT_TRANSFER_TAGS = [
    { tag: '{mitarbeiter}', label: 'Neuer Ansprechpartner (Vorname)' },
    { tag: '{mitarbeiter_name}', label: 'Neuer Ansprechpartner (voller Name)' },
    { tag: '{vorheriger}', label: 'Bisheriger Ansprechpartner (Vorname)' },
    { tag: '{vorheriger_name}', label: 'Bisheriger Ansprechpartner (voller Name)' },
];
const firstName = (name) => name.trim().split(/\s+/)[0] ?? '';
/** Setzt die Namen in den Weiterleitungstext ein. Ein leerer Text bedeutet den Standardtext, unbekannte Angaben in Klammern bleiben stehen. */
export function renderTransferText(template, next, previous) {
    const values = { mitarbeiter: firstName(next), mitarbeiter_name: next.trim(), vorheriger: firstName(previous), vorheriger_name: previous.trim() };
    return (template.trim() || CHAT_TRANSFER_DEFAULT).replace(/\{(\w+)\}/g, (all, key) => values[key] ?? all);
}
const minutes = (h, m) => Number(h) * 60 + Number(m);
/** Supportzeiten wie 08:00-22:00: Beginn vor dem Ende, höchstens bis 24:00 */
export function validChatHours(text) {
    const m = /^(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})$/.exec(text.trim());
    if (!m)
        return false;
    const from = minutes(m[1], m[2]);
    const to = minutes(m[3], m[4]);
    return Number(m[2]) < 60 && Number(m[4]) < 60 && from < to && to <= 24 * 60;
}
const optional = (schema) => z.union([z.literal(''), schema]);
/**
 * Einstellungen des Chat-Fensters. Leere Angaben heißen: Standard. Sie liegen beim Mandanten, das Fenster holt sie beim Laden von der API. So ändert der Mandant Aussehen, Text und
 * Supportzeiten im Backoffice, ohne etwas an seiner Webseite anzufassen. Mit `enabled: false` verschwindet das Fenster von allen Webseiten, ohne dass der Code entfernt werden muss.
 */
export const chatWidgetInput = z
    .object({
    enabled: z.boolean(),
    title: z.string().trim().max(60, 'Höchstens 60 Zeichen'),
    greeting: z.string().trim().max(300, 'Höchstens 300 Zeichen'),
    color: optional(z.string().trim().regex(/^#[0-9a-fA-F]{6}$/, 'Bitte geben Sie die Farbe so an: #0f766e')),
    privacyUrl: optional(z
        .string()
        .trim()
        .max(300, 'Die Adresse ist zu lang')
        .refine((v) => /^https:\/\/[^\s]+$/.test(v) || /^\/[^\s]*$/.test(v), 'Bitte geben Sie eine Adresse mit https:// an oder einen Pfad wie /datenschutz')),
    supportEmail: optional(z.string().trim().max(200).pipe(z.email('Bitte geben Sie eine gültige E-Mail-Adresse an'))),
    hours: optional(z.string().trim().refine(validChatHours, 'Bitte geben Sie die Zeiten so an: 08:00-22:00 (Beginn vor dem Ende)')),
    days: optional(z.string().trim().regex(/^[1-7]([-,][1-7])*$/, 'Bitte geben Sie die Tage so an: 1-5 oder 1,2,4 (1 ist Montag)')),
    transferText: z.string().trim().max(600, 'Höchstens 600 Zeichen'),
    timezone: optional(z.string().trim().max(60).regex(/^[A-Za-z][A-Za-z0-9_+-]*(\/[A-Za-z0-9_+-]+)*$/, 'Bitte geben Sie eine Zeitzone wie Europe/Berlin an')),
})
    .strict();
export const CHAT_WIDGET_EMPTY = { enabled: true, title: '', greeting: '', color: '', privacyUrl: '', supportEmail: '', hours: '', days: '', timezone: '', transferText: '' };
//# sourceMappingURL=embed.js.map